import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  FileQuestion,
  Home,
  LogIn,
  RefreshCw,
  ServerCrash,
  ShieldAlert,
  WifiOff,
} from "lucide-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import {
  classifyError,
  logError,
  type ClassifiedError,
} from "@/lib/errors/error-types";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

interface ErrorComponentProps {
  error: unknown;
  reset?: () => void;
  context?: {
    route?: string;
    userId?: string;
    action?: string;
  };
}

type ErrorType = ClassifiedError["type"];

/** Presentación por tipo de error: icono, tono, título y descripción amigable. */
const ERROR_PRESENTATION: Record<
  ErrorType,
  {
    icon: typeof AlertTriangle;
    tone: string;
    title: string;
    description: string;
  }
> = {
  auth: {
    icon: ShieldAlert,
    tone: "text-orange-600 bg-orange-500/10 ring-orange-500/20 dark:text-orange-400",
    title: "Tu sesión expiró",
    description:
      "Por seguridad cerramos tu sesión. Inicia sesión de nuevo para continuar donde te quedaste.",
  },
  permission: {
    icon: ShieldAlert,
    tone: "text-orange-600 bg-orange-500/10 ring-orange-500/20 dark:text-orange-400",
    title: "No tienes acceso a esta sección",
    description:
      "Tu rol no tiene permiso para ver esta página. Si crees que es un error, pide acceso a un administrador.",
  },
  network: {
    icon: WifiOff,
    tone: "text-blue-600 bg-blue-500/10 ring-blue-500/20 dark:text-blue-400",
    title: "Sin conexión",
    description:
      "No pudimos comunicarnos con el servidor. Revisa tu conexión a internet y vuelve a intentarlo.",
  },
  "not-found": {
    icon: FileQuestion,
    tone: "text-muted-foreground bg-muted ring-border",
    title: "No encontramos lo que buscas",
    description:
      "El registro o la página no existe, o fue eliminado. Revisa el enlace o vuelve a la página anterior.",
  },
  server: {
    icon: ServerCrash,
    tone: "text-destructive bg-destructive/10 ring-destructive/20",
    title: "El servidor tuvo un problema",
    description:
      "Algo falló de nuestro lado al procesar la solicitud. Inténtalo de nuevo en unos segundos.",
  },
  validation: {
    icon: AlertTriangle,
    tone: "text-amber-600 bg-amber-500/10 ring-amber-500/20 dark:text-amber-400",
    title: "Hay datos que no son válidos",
    description:
      "La información recibida no tiene el formato esperado. Revisa el detalle a continuación.",
  },
  unknown: {
    icon: AlertTriangle,
    tone: "text-destructive bg-destructive/10 ring-destructive/20",
    title: "Algo salió mal",
    description:
      "Ocurrió un error inesperado al cargar esta página. Puedes reintentar o volver atrás.",
  },
};

/**
 * Mensaje específico del error que vale la pena mostrar debajo de la
 * descripción. Para los tipos con mensaje genérico (auth, red, servidor…) el
 * clasificador ya lo reemplazó por un texto fijo que repetiría la descripción.
 */
function detailMessage(error: ClassifiedError): string | null {
  if (error.type !== "unknown" && error.type !== "validation") return null;
  const msg = error.message?.trim();
  if (!msg || msg === "Ha ocurrido un error inesperado.") return null;
  return msg;
}

function buildTechnicalInfo(
  error: ClassifiedError,
  context: ErrorComponentProps["context"],
): string {
  const original = error.originalError;
  return JSON.stringify(
    {
      type: error.type,
      message: error.message,
      route:
        context?.route ??
        (typeof window !== "undefined" ? window.location.pathname : undefined),
      url: typeof window !== "undefined" ? window.location.href : undefined,
      timestamp: new Date().toISOString(),
      error:
        original instanceof Error
          ? {
              name: original.name,
              message: original.message,
              stack: original.stack,
            }
          : original,
    },
    null,
    2,
  );
}

export function ErrorComponent({ error, reset, context }: ErrorComponentProps) {
  const navigate = useNavigate();
  const router = useRouter();
  // Memo por error: antes se re-clasificaba en cada render y el efecto de log
  // se disparaba en cada re-render del layout.
  const classifiedError = useMemo(() => classifyError(error), [error]);
  const presentation = ERROR_PRESENTATION[classifiedError.type];
  const Icon = presentation.icon;
  const detail = detailMessage(classifiedError);

  useEffect(() => {
    logError(classifiedError, context);
    // `context` es un literal nuevo en cada render del route; loguear una vez
    // por error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifiedError]);

  const canGoBack =
    typeof window !== "undefined" && window.history.length > 1;

  const handleRetry = () => {
    if (reset) {
      // Re-ejecuta los loaders de la ruta y limpia el error boundary.
      reset();
      void router.invalidate();
    } else {
      window.location.reload();
    }
  };

  const handleBack = () => {
    router.history.back();
  };

  const handleHome = () => {
    reset?.();
    navigate({ to: "/" });
  };

  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-md text-center animate-in fade-in-0 zoom-in-95 duration-300">
        <div
          className={cn(
            "mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ring-1",
            presentation.tone,
          )}
        >
          <Icon className="h-7 w-7" />
        </div>

        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {presentation.title}
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
          {presentation.description}
        </p>

        {detail && (
          <p className="mt-4 rounded-lg border border-border bg-muted/60 px-3 py-2 text-left font-mono text-xs leading-relaxed text-foreground/80 break-words line-clamp-4">
            {detail}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {classifiedError.requiresAuth ? (
            <Button onClick={() => navigate({ to: "/auth/sign-in" })}>
              <LogIn className="h-4 w-4" />
              Iniciar sesión
            </Button>
          ) : (
            <>
              {classifiedError.canRetry && (
                <Button onClick={handleRetry}>
                  <RefreshCw className="h-4 w-4" />
                  Reintentar
                </Button>
              )}
              {canGoBack && (
                <Button
                  variant={classifiedError.canRetry ? "outline" : "default"}
                  onClick={handleBack}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Volver
                </Button>
              )}
              <Button variant="ghost" onClick={handleHome}>
                <Home className="h-4 w-4" />
                Ir al inicio
              </Button>
            </>
          )}
        </div>

        {!classifiedError.requiresAuth && (
          <SupportDetails
            classifiedError={classifiedError}
            context={context}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Pie para soporte: copiar los detalles del error (siempre) y verlos
 * desplegados (solo en desarrollo, incluyen el stack).
 */
function SupportDetails({
  classifiedError,
  context,
}: {
  classifiedError: ClassifiedError;
  context: ErrorComponentProps["context"];
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const technicalInfo = useMemo(
    () => buildTechnicalInfo(classifiedError, context),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classifiedError],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(technicalInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="mt-8 border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 text-xs text-muted-foreground">
        <span>¿Persiste el problema?</span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded px-1 font-medium text-foreground/80 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? "Detalles copiados" : "Copia los detalles"}
        </button>
        <span>y compártelos con soporte.</span>
      </div>

      {import.meta.env.DEV && (
        <div className="mt-3 text-left">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mx-auto flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                open && "rotate-180",
              )}
            />
            Detalles técnicos
          </button>
          {open && (
            <pre className="mt-2 max-h-56 overflow-auto rounded-lg border border-border bg-muted/50 p-3 text-[11px] leading-relaxed text-muted-foreground">
              {technicalInfo}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
